import BugsnagPerformance from '@bugsnag/browser-performance';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginVue from '@bugsnag/plugin-vue';
import type { App } from 'vue';

import type { AppConfig } from '@/config/fetchEnv';
import { useUserStore } from '@/stores/user';

// Projet Bugsnag « welcome » — front de cette app.
// (Avant : la clé du projet « Auth », partagée avec af/auth.)
const BUGSNAG_API_KEY = '8254e44393b01cd2c1afb5545a8d3208';

const IGNORED_MESSAGES = [
  'Network Error',
  'Failed to fetch',
  'Load failed',
  'Connection closed',
  'Request aborted',
  'Unable to preload CSS for',
  'error loading dynamically imported module',
  'ResizeObserver loop completed with undelivered notifications',
  'ResizeObserver loop limit exceeded',
  'Non-Error promise rejection',
  'cannot have a negative time stamp'
];

function getReleaseStage(environment: string): string {
  return environment === 'production' ? 'production' : 'development';
}

function getErrorMessage(error: unknown): string {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }

  return '';
}

function isBrowserNoise(error: unknown): boolean {
  const message = getErrorMessage(error);
  return IGNORED_MESSAGES.some(ignoredMessage => message.includes(ignoredMessage));
}

/**
 * Une frame est-elle attribuée au document HTML lui-même — un chemin de route,
 * donc sans extension de fichier — plutôt qu'à un asset JS ?
 */
function isFrameInDocument(file: string): boolean {
  try {
    const { origin, pathname } = new URL(file, window.location.origin);
    return origin === window.location.origin && !/\.\w+$/.test(pathname);
  } catch {
    return false;
  }
}

/**
 * `index.html` ne contient aucun script inline : un seul
 * `<script type="module" src="/assets/…">`. Une stack entièrement attribuée au
 * document ne peut donc pas venir de notre code — ce sont les scripts que les
 * navigateurs in-app iOS (app Google, Chrome iOS) injectent dans la page pour
 * l'autofill ou la traduction, et qui remontent ici via `window.onerror`.
 * Ils arrivent sans source map et avec des lignes/colonnes qui n'existent dans
 * aucun de nos chunks.
 *
 * À revoir si un script inline est un jour ajouté au HTML.
 */
function isInjectedScriptError(frames: { file: string }[]): boolean {
  // `[native code]` et les frames sans fichier ne disent rien de l'origine.
  const located = frames.filter(frame => frame.file && frame.file !== '[native code]');
  return located.length > 0 && located.every(frame => isFrameInDocument(frame.file));
}

export default function installBugsnag(config: AppConfig) {
  return {
    install(app: App) {
      const userStore = useUserStore();

      Bugsnag.start({
        apiKey: BUGSNAG_API_KEY,
        plugins: [new BugsnagPluginVue()],
        releaseStage: getReleaseStage(config.environment),
        enabledReleaseStages: ['production'],
        autoDetectErrors: true,
        enabledBreadcrumbTypes: ['error', 'log', 'navigation', 'request'],
        onError(event) {
          if (isBrowserNoise(event.originalError)) {
            return false;
          }

          if (isInjectedScriptError(event.errors[0]?.stacktrace ?? [])) {
            return false;
          }

          const user = userStore.user;

          if (user) {
            event.setUser(user.uuid, user.email);
          }
        }
      });

      BugsnagPerformance.start({ apiKey: BUGSNAG_API_KEY });

      const bugsnagVuePlugin = Bugsnag.getPlugin('vue');

      if (bugsnagVuePlugin) {
        app.use(bugsnagVuePlugin);
      }
    }
  };
}
